// import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMoolah } from '../contexts/MoolahContext';
import { 
  ArrowLeft,
  X,
  Wallet,
  Building2,
  Check,
  Download,
  Info,
  Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { APP_CONFIG } from '../config/app-config';
import { getToken } from '../utils/authToken';
import { BeneficiaryList } from '../components/overlays/shared/BeneficiaryList';
import { ConfirmationModal } from '../components/overlays/shared/ConfirmationModal';
import { SaveContactPrompt } from '../components/SaveContactPrompt';
import { beneficiaryService, resolveContactMobile, type PaymentBeneficiary } from '../services/beneficiaryService';
import { findContactByMsisdn, findContactByBankAccount } from '../utils/saMsisdn';
import { ErrorModal } from '../components/ui/ErrorModal';
import {
  bankFieldModalCopy,
  validateSaBankAccountNumber,
  walletDestinationModalCopy,
} from '../utils/saBankAccountValidation';
import { useEffect } from 'react';

// Types for Request Money functionality
type AccountType = 'mymoolah' | 'bank';
type RequestStep = 'form' | 'confirmation' | 'success';
type RequestMode = 'once' | 'recurring';

interface RequestFormData {
  accountType: AccountType;
  payerName: string;
  payerMobileNumber: string;
  payerAccountNumber?: string;
  payerBankName?: string;
  amount: string;
  reference: string;
  frequency?: 'daily' | 'weekly' | 'monthly';
  startDate?: string;
  startTime?: string;
  endOption?: 'never' | 'count' | 'until';
  occurrences?: string;
  untilDate?: string;
  dayOfWeek?: string;
  dayOfMonth?: string;
}

interface MoneyRequest {
  id: string;
  type: 'money_request';
  accountType: AccountType;
  requesterId: string;
  requesterName: string;
  payerName: string;
  payerMobileNumber: string;
  payerAccountNumber?: string;
  payerBankName?: string;
  amount: number;
  reference: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  createdAt: string;
  expiresAt: string;
}

// Normalize SA mobile to 27XXXXXXXXX
const normalizeSAMobile = (v: string): string => {
  const s = (v || '').replace(/\s/g, '');
  if (s.startsWith('+27')) return s.slice(1);
  if (s.startsWith('0')) return '27' + s.slice(1);
  return s;
};

// Helpers to format local date/time for input defaults
const formatLocalDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const formatLocalTime = (d: Date): string => {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

// Get bank code for Peach Payments API
const getBankCode = (bankName: string): string => {
  const bankCodes: { [key: string]: string } = {
    'ABSA Bank': '632005',
    'African Bank': '430000',
    'Bidvest Bank': '462005',
    'Capitec Bank': '470010',
    'Discovery Bank': '679000',
    'First National Bank (FNB)': '250655',
    'Investec Bank': '580105',
    'Nedbank': '198765',
    'Standard Bank': '051001',
    'TymeBank': '678910'
  };
  return bankCodes[bankName] || '';
};

const BANK_ACCOUNT_RULES: { [key: string]: { min: number; max: number; label: string } } = {
  'ABSA Bank':                    { min: 9,  max: 10, label: '9 or 10' },
  'Standard Bank':                { min: 9,  max: 11, label: '9 or 11' },
  'Capitec Bank':                 { min: 10, max: 10, label: '10' },
  'First National Bank (FNB)':    { min: 11, max: 11, label: '11' },
  'Nedbank':                      { min: 10, max: 10, label: '10' },
  'Discovery Bank':               { min: 10, max: 11, label: '10 or 11' },
  'TymeBank':                     { min: 10, max: 11, label: '10 or 11' },
  'African Bank':                 { min: 10, max: 11, label: '10 or 11' },
  'Investec Bank':                { min: 11, max: 11, label: '11' },
  'Bidvest Bank':                 { min: 10, max: 11, label: '10 or 11' },
};

const BANK_ACCOUNT_MAX_LENGTHS: { [key: string]: number } = Object.fromEntries(
  Object.entries(BANK_ACCOUNT_RULES).map(([k, v]) => [k, v.max])
);

const normalizeAccountNumber = (acctNum: string, bankName?: string): string => {
  const digits = acctNum.replace(/\D/g, '');
  if (!digits) return acctNum;
  const maxLen = (bankName && BANK_ACCOUNT_MAX_LENGTHS[bankName]) || 11;
  if (digits.length > maxLen && digits.startsWith('0')) {
    const stripped = digits.replace(/^0+/, '');
    return stripped.length < maxLen ? digits.slice(digits.length - maxLen) : stripped;
  }
  return digits;
};

export function RequestMoneyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshBalanceAfterAction } = useMoolah();
  const [currentStep, setCurrentStep] = useState<RequestStep>('form');
  const [requestMode, setRequestMode] = useState<RequestMode>('once');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [createdRequest, setCreatedRequest] = useState<MoneyRequest | null>(null);
  const [recentPayers, setRecentPayers] = useState<any[]>([]);
  const [loadingPayers, setLoadingPayers] = useState<boolean>(false);
  const [showRemovePayerModal, setShowRemovePayerModal] = useState(false);
  const [payerToRemove, setPayerToRemove] = useState<{ id: string; name: string } | null>(null);
  // Saved payer (shared contact book) removal — clears the payer role only,
  // never deletes the contact or affects the Send-Money beneficiary.
  const [showRemoveSavedPayerModal, setShowRemoveSavedPayerModal] = useState(false);
  const [savedPayerToRemove, setSavedPayerToRemove] = useState<{ id: string; name: string } | null>(null);
  // Saved payers (shared unified contact book — same source as Send Money beneficiaries)
  const [savedPayers, setSavedPayers] = useState<PaymentBeneficiary[]>([]);
  const [loadingSavedPayers, setLoadingSavedPayers] = useState<boolean>(false);
  // When a selected payer has more than one payment account, let the user choose which one
  const [selectedPayer, setSelectedPayer] = useState<PaymentBeneficiary | null>(null);
  const [selectedPayerAccountId, setSelectedPayerAccountId] = useState<number | null>(null);
  // Post-request "Save Payer?" prompt
  const [payerToSave, setPayerToSave] = useState<{
    name: string;
    accountType: 'mymoolah' | 'bank';
    msisdn?: string;
    bankName?: string;
    accountNumber?: string;
  } | null>(null);
  const [identifierError, setIdentifierError] = useState<{ title: string; message: string } | null>(null);

  // Payshap participating banks (alphabetically ordered)
  const payshapBanks = [
    'ABSA Bank',
    'African Bank',
    'Bidvest Bank',
    'Capitec Bank',
    'Discovery Bank',
    'First National Bank (FNB)',
    'Investec Bank',
    'Nedbank',
    'Standard Bank',
    'TymeBank'
  ];
  
  const [formData, setFormData] = useState<RequestFormData>({
    accountType: 'mymoolah',
    payerName: '',
    payerMobileNumber: '',
    payerAccountNumber: '',
    payerBankName: '',
    amount: '',
    reference: '',
    frequency: 'monthly',
    startDate: formatLocalDate(new Date()),
    startTime: formatLocalTime(new Date()),
    endOption: 'never',
    occurrences: '',
    untilDate: '',
    dayOfWeek: '1',
    dayOfMonth: '1'
  });

  const [errors, setErrors] = useState<Partial<RequestFormData>>({});

  // Load recent payers for quick selection
  useEffect(() => {
    (async () => {
      try {
        setLoadingPayers(true);
        const token = getToken();
        const api = APP_CONFIG.API.baseUrl;
        const resp = await fetch(`${api}/api/v1/requests/recent-payers`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        });
        const j = await resp.json();
        if (resp.ok && j?.success && Array.isArray(j?.data?.recentPayers)) {
          setRecentPayers(j.data.recentPayers);
        } else {
          setRecentPayers([]);
        }
      } catch (_) {
        setRecentPayers([]);
      } finally {
        setLoadingPayers(false);
      }
    })();
  }, []);

  // Load saved payers (unified beneficiaries) for quick re-use, same as Send Money.
  const loadSavedPayers = async () => {
    try {
      setLoadingSavedPayers(true);
      const list = await beneficiaryService.getPayerBeneficiaries();
      setSavedPayers(list);
    } catch (_) {
      setSavedPayers([]);
    } finally {
      setLoadingSavedPayers(false);
    }
  };

  useEffect(() => {
    void loadSavedPayers();
  }, []);

  // Only payment-type accounts (wallet / bank) are relevant for requesting money.
  const getPayerPaymentAccounts = (b: PaymentBeneficiary) =>
    (b.accounts || []).filter((a) => a.type === 'mymoolah' || a.type === 'bank');

  // Fill the form from a saved payer's chosen account.
  const applyPayerAccount = (b: PaymentBeneficiary, account?: any) => {
    const acc = account || null;
    const accType = acc?.type || b.accountType;
    // Resolve the real contact mobile (never the bank account number). For bank
    // contacts this comes from the bank account's mobileNumber; for wallet
    // contacts it is the wallet phone.
    const mobile = resolveContactMobile(b, acc);

    if (accType === 'bank') {
      const bankName = acc?.bankName || acc?.metadata?.bankName || b.bankName || '';
      const accountNumber = acc?.accountNumber || acc?.identifier || '';
      setFormData((prev) => ({
        ...prev,
        accountType: 'bank',
        payerName: b.name,
        payerMobileNumber: mobile ? normalizeSAMobile(mobile) : '',
        payerBankName: bankName,
        payerAccountNumber: accountNumber,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        accountType: 'mymoolah',
        payerName: b.name,
        payerMobileNumber: mobile ? normalizeSAMobile(mobile) : '',
        payerBankName: '',
        payerAccountNumber: '',
      }));
    }
    setErrors({});
  };

  // Select a saved payer from the list. If they have multiple accounts and no explicit
  // accountId was provided, surface an account chooser; otherwise apply directly.
  const handleSelectSavedPayer = (b: PaymentBeneficiary, accountId?: number) => {
    const accounts = getPayerPaymentAccounts(b);
    if (accountId) {
      const acc = accounts.find((a) => a.id === accountId);
      setSelectedPayer(accounts.length > 1 ? b : null);
      setSelectedPayerAccountId(acc?.id ?? null);
      applyPayerAccount(b, acc);
      return;
    }
    if (accounts.length <= 1) {
      const acc = accounts[0];
      setSelectedPayer(null);
      setSelectedPayerAccountId(acc?.id ?? null);
      applyPayerAccount(b, acc);
      return;
    }
    // Multiple accounts: default to the default/first, and show the chooser.
    const defaultAcc = accounts.find((a) => a.isDefault) || accounts[0];
    setSelectedPayer(b);
    setSelectedPayerAccountId(defaultAcc?.id ?? null);
    applyPayerAccount(b, defaultAcc);
  };

  const handleInputChange = (field: keyof RequestFormData, value: string) => {
    // For amount field, preserve exact value as string to prevent browser auto-correction
    if (field === 'amount') {
      // Allow empty string, numbers, and single decimal point
      const cleanValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = cleanValue.split('.');
      const sanitized = parts.length > 2 
        ? parts[0] + '.' + parts.slice(1).join('')
        : cleanValue;
      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        const limited = parts[0] + '.' + parts[1].substring(0, 2);
        setFormData(prev => ({ ...prev, [field]: limited }));
      } else {
        setFormData(prev => ({ ...prev, [field]: sanitized }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAccountTypeSelect = (type: AccountType) => {
    setFormData(prev => ({
      ...prev,
      accountType: type,
      // Clear type-specific fields when switching
      payerAccountNumber: type === 'mymoolah' ? '' : prev.payerAccountNumber,
      payerBankName: type === 'mymoolah' ? '' : prev.payerBankName
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<RequestFormData> = {};
    let identifierModal: { title: string; message: string } | null = null;

    if (!formData.payerName.trim()) {
      newErrors.payerName = 'Payer name is required';
    }

    if (!formData.payerMobileNumber.trim()) {
      newErrors.payerMobileNumber = 'Payer mobile number is required';
    } else {
      const destCopy = walletDestinationModalCopy(formData.payerMobileNumber);
      if (destCopy) {
        newErrors.payerMobileNumber = destCopy.message;
        identifierModal = destCopy;
      } else {
        const phoneRegex = /^(\+27|27|0)[6-8][0-9]{8}$/;
        if (!phoneRegex.test(formData.payerMobileNumber.replace(/\s/g, ''))) {
          newErrors.payerMobileNumber = 'Please enter a valid South African mobile number';
        } else if (formData.accountType === 'mymoolah' && user?.phoneNumber) {
          const userNorm = normalizeSAMobile(user.phoneNumber);
          const payerNorm = normalizeSAMobile(formData.payerMobileNumber);
          if (userNorm && payerNorm && userNorm === payerNorm) {
            newErrors.payerMobileNumber = 'You cannot request money from yourself';
          }
        }
      }
    }

    if (formData.accountType === 'bank') {
      if (!formData.payerBankName?.trim()) {
        newErrors.payerBankName = 'Please select the payer\'s bank';
      }

      if (!formData.payerAccountNumber?.trim()) {
        newErrors.payerAccountNumber = 'Please enter the account number';
      } else {
        const acctCheck = validateSaBankAccountNumber(formData.payerAccountNumber, {
          bankName: formData.payerBankName,
        });
        if (!acctCheck.valid) {
          newErrors.payerAccountNumber = acctCheck.error || 'Invalid account number';
          identifierModal = bankFieldModalCopy(acctCheck) || identifierModal;
        }
      }
    }

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        newErrors.amount = 'Please enter a valid amount';
      } else if (formData.accountType === 'bank') {
        // PayShap RTP: minimum R10
        if (amount < 10) {
          newErrors.amount = 'Minimum bank request is R10.00';
        } else if (amount > 50000) {
          newErrors.amount = 'Maximum request amount is R50,000.00';
        }
      } else if (amount < 5) {
        newErrors.amount = 'Minimum request amount is R5.00';
      } else if (amount > 50000) {
        newErrors.amount = 'Maximum request amount is R50,000.00';
      }
    }

    // Recurring validation
    if (requestMode === 'recurring') {
      if (!formData.startDate) newErrors.startDate = 'Start date is required';
      if (!formData.startTime) newErrors.startTime = 'Start time is required';
      if (formData.frequency === 'weekly' && !formData.dayOfWeek) newErrors.dayOfWeek = 'Select a weekday';
      if (formData.frequency === 'monthly' && !formData.dayOfMonth) newErrors.dayOfMonth = 'Select a day of month';
      if (formData.endOption === 'count') {
        const n = parseInt(formData.occurrences || '0', 10);
        if (!n || n < 1 || n > 60) newErrors.occurrences = 'Enter 1–60 occurrences';
      }
      if (formData.endOption === 'until' && !formData.untilDate) newErrors.untilDate = 'Select an end date';
    }

    setErrors(newErrors);
    setIdentifierError(identifierModal);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    if (showDialog) {
      setShowDialog(false);
    } else {
      navigate('/transact');
    }
  };

  const generateRequestId = (): string => {
    return `REQ${Date.now().toString().slice(-8).toUpperCase()}`;
  };

  // After a successful request, offer to save the payer — but only if they are not
  // already a saved contact (normalized match, so the prompt never repeats).
  const maybePromptSavePayer = () => {
    const name = formData.payerName?.trim();
    if (!name) return;
    if (formData.accountType === 'bank') {
      const acctNumber = normalizeAccountNumber(formData.payerAccountNumber || '', formData.payerBankName);
      if (!findContactByBankAccount(savedPayers, acctNumber)) {
        setPayerToSave({
          name,
          accountType: 'bank',
          msisdn: formData.payerMobileNumber,
          bankName: formData.payerBankName,
          accountNumber: acctNumber,
        });
      }
    } else if (!findContactByMsisdn(savedPayers, formData.payerMobileNumber)) {
      setPayerToSave({ name, accountType: 'mymoolah', msisdn: formData.payerMobileNumber });
    }
  };

  // Persist the payer to the shared unified contact book (reused by Send Money too).
  const persistPayerToSave = async () => {
    if (!payerToSave) return;
    if (payerToSave.accountType === 'bank') {
      await beneficiaryService.createPaymentBeneficiary({
        name: payerToSave.name,
        accountType: 'bank',
        bankName: payerToSave.bankName,
        accountNumber: payerToSave.accountNumber,
        mobile: payerToSave.msisdn,
        role: 'payer',
      });
    } else {
      await beneficiaryService.createPaymentBeneficiary({
        name: payerToSave.name,
        accountType: 'mymoolah',
        msisdn: payerToSave.msisdn,
        role: 'payer',
      });
    }
    await loadSavedPayers();
  };

  const handleRequestMoney = async () => {
    if (!validateForm()) {
      return;
    }

    // KYC: only required for bank requests (tiered). MMwallet requests are tier 0 (no KYC).
    if (formData.accountType === 'bank') {
      if (!user?.kycVerified) {
        navigate('/kyc/documents?returnTo=/request-money');
        return;
      }
    }

    if (!user) {
      console.error('User not authenticated');
      return;
    }

    setIsProcessing(true);
    setCurrentStep('confirmation');

    try {
      const amount = parseFloat(formData.amount);
      const requestId = generateRequestId();
      const expiresAt = new Date(Date.now() + (24 * 60 * 60 * 1000)); // 24 hours from now

      // Create the money request object
      const moneyRequest: MoneyRequest = {
        id: requestId,
        type: 'money_request',
        accountType: formData.accountType,
        requesterId: user.id,
        requesterName: user.name,
        payerName: formData.payerName,
        payerMobileNumber: formData.payerMobileNumber,
        payerAccountNumber: formData.payerAccountNumber,
        payerBankName: formData.payerBankName,
        amount: amount,
        reference: formData.reference || `Payment request from ${user.name}`,
        status: 'pending',
        createdAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString()
      };

      

      if (formData.accountType === 'mymoolah') {
        // MyMoolah internal request – call backend API
        const token = getToken();
        const api = APP_CONFIG.API.baseUrl;

        const resp = await fetch(`${api}/api/v1/requests/wallet`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            payerPhoneNumber: normalizeSAMobile(formData.payerMobileNumber),
            amount,
            description: formData.reference || `Payment request from ${user.name}`,
          }),
        });

        const payload = await resp.json();
        if (!resp.ok || !payload?.success || !payload?.data?.requestId) {
          throw new Error(payload?.message || 'Failed to create payment request');
        }

        // Reflect backend request id in local success view
        moneyRequest.id = String(payload.data.requestId);
      } else {
        // Standard Bank PayShap RTP (Request to Pay)
        const token = getToken();
        const api = APP_CONFIG.API.baseUrl;

        const resp = await fetch(`${api}/api/v1/standardbank/payshap/rtp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: amount,
            currency: 'ZAR',
            payerName: formData.payerName,
            payerMobileNumber: normalizeSAMobile(formData.payerMobileNumber),
            payerAccountNumber: normalizeAccountNumber(formData.payerAccountNumber || '', formData.payerBankName),
            payerBankName: formData.payerBankName,
            description: formData.reference || `Money request from ${user.name}`,
          }),
        });

        const payload = await resp.json();

        if (!resp.ok || !payload?.success) {
          const errorMessage = payload?.message || payload?.error || 'Failed to submit PayShap Request to Pay';
          throw new Error(errorMessage);
        }

        moneyRequest.id = String(payload.data?.merchantTransactionId || requestId);
      }

      setCreatedRequest(moneyRequest);
      setCurrentStep('success');

      // Offer to save the payer for next time (only if new).
      maybePromptSavePayer();

      // Event-driven balance refresh after payment request creation
      try {
        await refreshBalanceAfterAction('payment_request_created');
      } catch (error) {
        console.error('Balance refresh failed after request creation:', error);
        // Don't break the success flow if balance refresh fails
      }

    } catch (error) {
      console.error('Request money failed:', error);
      const displayMessage = (error instanceof Error ? error.message : null) || 'Unable to send payment request. Please try again.';
      try { alert(displayMessage); } catch (_) { /* no-op */ }
      setCurrentStep('form');
      setShowDialog(false);
      
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amount: string | number): string => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return isNaN(numAmount) ? 'R0.00' : `R${numAmount.toFixed(2)}`;
  };

  const getNotificationMessage = () => {
    if (requestMode === 'recurring') {
      if (formData.accountType === 'mymoolah') {
        return `Recurring payment requests will be sent to ${formData.payerName} according to your schedule.`;
      } else {
        return `Recurring payment requests will be submitted to ${formData.payerBankName} according to your schedule.`;
      }
    } else {
      if (formData.accountType === 'mymoolah') {
        return `${formData.payerName} will receive a notification to approve your payment request.`;
      } else {
        return `A payment request will be submitted to ${formData.payerBankName} for processing. Share your MSISDN (${user?.phoneNumber}) with the payer for automatic wallet allocation.`;
      }
    }
  };

  if (currentStep === 'success') {
    return (
      <div style={{ 
        fontFamily: 'Montserrat, sans-serif',
        backgroundColor: '#ffffff',
        padding: '16px',
        minHeight: 'calc(100vh - 160px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#f0fdf4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px'
        }}>
          <Check style={{ width: '40px', height: '40px', color: '#16a34a' }} />
        </div>
        
        <h1 style={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '24px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '8px'
        }}>
          {requestMode === 'recurring' ? 'Recurring Request Created!' : 'Request Sent!'}
        </h1>
        
        <p style={{
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '16px',
          color: '#6b7280',
          marginBottom: '24px',
          lineHeight: '1.5'
        }}>
          {requestMode === 'recurring' 
            ? `Your recurring payment request for ${formatCurrency(formData.amount)} has been scheduled for ${formData.payerName}.`
            : `Your payment request for ${formatCurrency(formData.amount)} has been sent to ${formData.payerName}.`
          }
        </p>

        {/* 🆕 NEW: MSISDN Reference Information for Bank Requests */}
        {formData.accountType === 'bank' && (
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #0ea5e9',
            borderRadius: '12px',
            padding: '16px',
            width: '100%',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#0ea5e9', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <span style={{ color: 'white', fontSize: '12px' }}>📱</span>
              </div>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '600',
                color: '#0c4a6e',
                margin: 0
              }}>
                Automatic Wallet Allocation
              </h3>
            </div>
            <p style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              color: '#0369a1',
              marginBottom: '12px',
              lineHeight: '1.5'
            }}>
              Share your MSISDN reference with the payer:
            </p>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #0ea5e9',
              borderRadius: '8px',
              padding: '12px',
              textAlign: 'center',
              marginBottom: '12px'
            }}>
              <code style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#0c4a6e',
                fontFamily: 'monospace'
              }}>
                {user?.phoneNumber || 'MSISDN not available'}
              </code>
            </div>
            <p style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '12px',
              color: '#0369a1',
              margin: 0,
              fontStyle: 'italic'
            }}>
              💡 The bank will automatically route the payment to your wallet using this reference
            </p>
          </div>
        )}

        {createdRequest && (
          <div style={{
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            padding: '16px',
            width: '100%',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
              <span style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {requestMode === 'recurring' ? 'Schedule ID' : 'Request ID'}
              </span>
              <span style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#1f2937'
              }}>
                {createdRequest.id}
              </span>
            </div>
            
            {requestMode === 'recurring' && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px'
              }}>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  Frequency
                </span>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#1f2937'
                }}>
                  {formData.frequency ? formData.frequency.charAt(0).toUpperCase() + formData.frequency.slice(1) : 'Unknown'}
                </span>
              </div>
            )}
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
              <span style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {requestMode === 'recurring' ? 'Next Request' : 'Expires'}
              </span>
              <span style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#1f2937'
              }}>
                {requestMode === 'recurring' 
                  ? `${formData.startDate} at ${formData.startTime}`
                  : `${new Date(createdRequest.expiresAt).toLocaleDateString()} at ${new Date(createdRequest.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                }
              </span>
            </div>
            
            <p style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              color: '#374151',
              margin: '12px 0 0 0',
              lineHeight: '1.5'
            }}>
              {getNotificationMessage()}
            </p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <Button 
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '16px',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              minHeight: '44px'
            }}
          >
            Back to Dashboard
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => navigate('/transaction-history')}
            style={{
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '16px',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              minHeight: '44px'
            }}
          >
            View Request History
          </Button>
        </div>

        {/* Post-request: shared Save Payer prompt (persists to unified contacts) */}
        <SaveContactPrompt
          isOpen={!!payerToSave}
          contactName={payerToSave?.name}
          roleLabel="payer"
          outcomeText="Request sent"
          onSave={persistPayerToSave}
          onClose={() => setPayerToSave(null)}
        />
      </div>
    );
  }

  return (
    <div style={{ 
      fontFamily: 'Montserrat, sans-serif',
      backgroundColor: '#ffffff',
      padding: '16px',
      minHeight: 'calc(100vh - 160px)'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '24px',
        position: 'relative'
      }}>
        <button
          onClick={() => navigate('/transact')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            position: 'absolute',
            left: '0',
            zIndex: 1
          }}
        >
          <ArrowLeft style={{ width: '20px', height: '20px', color: '#6b7280' }} />
        </button>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <h1 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '20px',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0
          }}>
            Request Money
          </h1>
        </div>
      </div>

      {currentStep === 'form' && (
        <>
          {/* 🆕 NEW: MSISDN Reference Display for Automatic Wallet Allocation */}
          <div style={{ 
            backgroundColor: '#f0f9ff', 
            border: '1px solid #0ea5e9', 
            borderRadius: '12px', 
            padding: '16px', 
            marginBottom: '24px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ 
                width: '24px', 
                height: '24px', 
                backgroundColor: '#0ea5e9', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>📱</span>
              </div>
              <h3 style={{ 
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '16px',
                fontWeight: '600',
                color: '#0c4a6e',
                margin: 0
              }}>
                Your Reference Number (MSISDN)
              </h3>
            </div>
            <p style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              color: '#0369a1',
              marginBottom: '12px',
              lineHeight: '1.5'
            }}>
              Share this number with the payer for automatic wallet allocation:
            </p>
            <div style={{ 
              backgroundColor: 'white', 
              border: '2px solid #0ea5e9', 
              borderRadius: '8px', 
              padding: '12px 16px',
              textAlign: 'center',
              marginBottom: '12px'
            }}>
              <code style={{ 
                fontSize: '20px', 
                fontWeight: 'bold', 
                color: '#0c4a6e',
                fontFamily: 'monospace',
                letterSpacing: '1px'
              }}>
                {user?.phoneNumber || 'MSISDN not available'}
              </code>
            </div>
            <p style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '12px',
              color: '#0369a1',
              margin: 0,
              fontStyle: 'italic'
            }}>
              💡 The payer's bank will automatically route the payment to your wallet using this reference
            </p>
          </div>

          {/* Saved payers (shared contact book) */}
          {(savedPayers.length > 0 || loadingSavedPayers) && (
            <div style={{ marginBottom: '16px' }}>
              <Label style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px',
                display: 'block'
              }}>
                Payers
              </Label>
              <BeneficiaryList
                title="Select Payer"
                type="all"
                beneficiaries={savedPayers.map((p) => ({
                  id: String(p.id),
                  name: p.name,
                  msisdn: p.msisdn,
                  identifier: p.identifier,
                  accountType: p.accountType,
                  accounts: getPayerPaymentAccounts(p).map((a) => ({
                    id: a.id,
                    type: a.type,
                    identifier: a.identifier,
                    label:
                      a.type === 'bank'
                        ? `${a.bankName || a.metadata?.bankName || 'Bank'} ••••${String(a.accountNumber || a.identifier || '').slice(-4)}`
                        : 'MyMoolah Wallet',
                    isDefault: !!a.isDefault,
                    metadata: a.metadata,
                  })),
                  isFavorite: p.isFavorite || false,
                  timesPaid: p.timesPaid || 0,
                  createdAt: p.createdAt || new Date().toISOString(),
                  updatedAt: p.updatedAt || new Date().toISOString(),
                })) as any}
                selectedBeneficiary={null}
                selectedAccountId={selectedPayerAccountId}
                onSelect={(b: any, accountId?: number) => {
                  const orig = savedPayers.find((p) => String(p.id) === String(b?.id));
                  if (orig) handleSelectSavedPayer(orig, accountId);
                }}
                onAddNew={() => {
                  try { document.getElementById('payerName')?.focus(); } catch (_) { /* no-op */ }
                }}
                onEdit={(b: any) => {
                  const orig = savedPayers.find((p) => String(p.id) === String(b?.id));
                  if (orig) handleSelectSavedPayer(orig);
                }}
                onRemove={(b: any) => {
                  // Remove from the Payers list only — clears the payer role,
                  // keeping the contact and any Send-Money beneficiary intact.
                  setSavedPayerToRemove({ id: String(b?.id), name: b?.name || 'this payer' });
                  setShowRemoveSavedPayerModal(true);
                }}
                isLoading={loadingSavedPayers}
                searchPlaceholder="Search payers"
                addNewButtonText="Add New Payer"
                showFilters={false}
              />

              {/* Account chooser for payers with more than one payment account */}
              {selectedPayer && getPayerPaymentAccounts(selectedPayer).length > 1 && (
                <div style={{ marginTop: '8px' }}>
                  <Label style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    display: 'block'
                  }}>
                    Request from account
                  </Label>
                  <Select
                    value={selectedPayerAccountId ? String(selectedPayerAccountId) : ''}
                    onValueChange={(v) => {
                      const id = Number(v);
                      const acc = getPayerPaymentAccounts(selectedPayer).find((a) => a.id === id);
                      setSelectedPayerAccountId(id);
                      applyPayerAccount(selectedPayer, acc);
                    }}
                  >
                    <SelectTrigger style={{ height: '44px', borderRadius: '12px', backgroundColor: '#f9fafb', border: '1px solid #d1d5db', fontFamily: 'Montserrat, sans-serif', fontSize: '14px' }}>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {getPayerPaymentAccounts(selectedPayer).map((a) => (
                        <SelectItem key={a.id} value={String(a.id)} style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px' }}>
                          {a.type === 'bank'
                            ? `${a.bankName || a.metadata?.bankName || 'Bank'} ••••${String(a.accountNumber || a.identifier || '').slice(-4)}`
                            : 'MyMoolah Wallet'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Payer Quick Pick */}
          {recentPayers.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <Label style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px',
                display: 'block'
              }}>
                Recent payers
              </Label>
              <BeneficiaryList
                title="Select Payer"
                type="all"
                beneficiaries={recentPayers
                  .map((p, idx) => ({
                  id: String(p.payerUserId || idx),
                  name: p.name || (p.phoneNumber || 'Unknown'),
                  identifier: p.phoneNumber || p.accountNumber || '',
                  accountType: 'mymoolah',
                  timesPaid: p.count || 0,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date(p.lastRequestedAt || Date.now()).toISOString(),
                }))}
                selectedBeneficiary={null}
                onSelect={(b) => {
                  setSelectedPayer(null);
                  setSelectedPayerAccountId(null);
                  handleInputChange('payerName', b.name);
                  handleInputChange('payerMobileNumber', (b as any).identifier || (b as any).msisdn || '');
                }}
                onAddNew={() => {
                  const el = document.getElementById('payerName');
                  try { el?.focus(); } catch (_) { /* no-op */ }
                }}
                onEdit={(b) => {
                  setSelectedPayer(null);
                  setSelectedPayerAccountId(null);
                  handleInputChange('payerName', b.name);
                  handleInputChange('payerMobileNumber', (b as any).identifier || (b as any).msisdn || '');
                  try { document.getElementById('payerMobileNumber')?.focus(); } catch (_) { /* no-op */ }
                }}
                onRemove={(b) => {
                  // Show confirmation modal before removing
                  setPayerToRemove({ id: typeof b.id === 'string' ? b.id : b.id.toString(), name: b.name });
                  setShowRemovePayerModal(true);
                }}
                isLoading={loadingPayers}
                searchPlaceholder="Search recent payers"
                addNewButtonText="Add New Payer"
                showFilters={false}
              />
            </div>
          )}

          {/* Mode Selection */}
          <div style={{ marginBottom: '16px' }}>
            <Label style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px',
              display: 'block'
            }}>
              Request Mode
            </Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <button
                onClick={() => setRequestMode('once')}
                style={{
                  padding: '12px',
                  borderRadius: '12px',
                  border: `2px solid ${requestMode === 'once' ? '#86BE41' : '#e5e7eb'}`,
                  backgroundColor: requestMode === 'once' ? '#f0fdf4' : '#ffffff',
                  cursor: 'pointer'
                }}
              >
                Once off
              </button>
              <button
                onClick={() => setRequestMode('recurring')}
                style={{
                  padding: '12px',
                  borderRadius: '12px',
                  border: `2px solid ${requestMode === 'recurring' ? '#86BE41' : '#e5e7eb'}`,
                  backgroundColor: requestMode === 'recurring' ? '#f0fdf4' : '#ffffff',
                  cursor: 'pointer'
                }}
              >
                Recurring
              </button>
            </div>
          </div>
          {/* Account Type Selection */}
          <div style={{ marginBottom: '24px' }}>
            <Label style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px',
              display: 'block'
            }}>
              Request From Account Type
            </Label>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {/* MyMoolah Account Type */}
              <button
                onClick={() => handleAccountTypeSelect('mymoolah')}
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  border: `2px solid ${formData.accountType === 'mymoolah' ? '#86BE41' : '#e5e7eb'}`,
                  backgroundColor: formData.accountType === 'mymoolah' ? '#f0fdf4' : '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  minHeight: '44px'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: '#86BE41',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Wallet style={{ width: '16px', height: '16px', color: 'white' }} />
                </div>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: formData.accountType === 'mymoolah' ? '#16a34a' : '#374151'
                }}>
                  MyMoolah
                </span>
              </button>

              {/* Bank Account Type */}
              <button
                onClick={() => handleAccountTypeSelect('bank')}
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  border: `2px solid ${formData.accountType === 'bank' ? '#86BE41' : '#e5e7eb'}`,
                  backgroundColor: formData.accountType === 'bank' ? '#f0fdf4' : '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  minHeight: '44px',
                  position: 'relative'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: '#2D8CCA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building2 style={{ width: '16px', height: '16px', color: 'white' }} />
                </div>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: formData.accountType === 'bank' ? '#16a34a' : '#374151'
                }}>
                  Bank
                </span>
                {/* KYC Required Badge for Bank option */}
                {!user?.kycVerified && (
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    fontSize: '8px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: '500',
                    padding: '2px 4px',
                    borderRadius: '4px'
                  }}>
                    KYC
                  </div>
                )}
              </button>
            </div>
            
            {/* KYC Information for Bank selection */}
            {formData.accountType === 'bank' && !user?.kycVerified && (
              <Alert style={{
                borderColor: '#fed7aa',
                backgroundColor: '#fff7ed',
                marginTop: '12px',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <Info style={{ width: '16px', height: '16px', color: '#ea580c' }} />
                <AlertDescription>
                  <span style={{ 
                    fontFamily: 'Montserrat, sans-serif', 
                    fontSize: '14px',
                    color: '#ea580c'
                  }}>
                    Bank requests require identity verification. Complete KYC to enable bank requests.
                  </span>
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Form Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Payer Name */}
            <div>
              <Label htmlFor="payerName" style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px',
                display: 'block'
              }}>
                Request From (Payer Name)
              </Label>
              <Input
                id="payerName"
                type="text"
                placeholder="Enter payer's full name"
                value={formData.payerName}
                onChange={(e) => handleInputChange('payerName', e.target.value)}
                style={{
                  height: '44px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderRadius: '12px',
                  border: `1px solid ${errors.payerName ? '#dc2626' : '#d1d5db'}`,
                  backgroundColor: '#f9fafb'
                }}
              />
              {errors.payerName && (
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: '#dc2626',
                  margin: '4px 0 0 0'
                }}>
                  {errors.payerName}
                </p>
              )}
            </div>

            {/* Payer Mobile Number */}
            <div>
              <Label htmlFor="payerMobileNumber" style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px',
                display: 'block'
              }}>
                Payer Mobile Number
              </Label>
              <Input
                id="payerMobileNumber"
                type="tel"
                placeholder="27XXXXXXXXX"
                value={formData.payerMobileNumber}
                onChange={(e) => handleInputChange('payerMobileNumber', e.target.value)}
                style={{
                  height: '44px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderRadius: '12px',
                  border: `1px solid ${errors.payerMobileNumber ? '#dc2626' : '#d1d5db'}`,
                  backgroundColor: '#f9fafb'
                }}
              />
              {errors.payerMobileNumber && (
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: '#dc2626',
                  margin: '4px 0 0 0'
                }}>
                  {errors.payerMobileNumber}
                </p>
              )}
            </div>

            {/* Bank-specific fields */}
            {formData.accountType === 'bank' && (
              <>
                <div>
                  <Label htmlFor="payerBankName" style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    display: 'block'
                  }}>
                    Payer Bank Name
                  </Label>
                  <Select 
                    value={formData.payerBankName || ''}
                    onValueChange={(value) => handleInputChange('payerBankName', value)}
                  >
                    <SelectTrigger 
                      style={{
                        height: '44px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        fontWeight: '400',
                        borderRadius: '12px',
                        border: `1px solid ${errors.payerBankName ? '#dc2626' : '#d1d5db'}`,
                        backgroundColor: '#f9fafb'
                      }}
                    >
                      <SelectValue placeholder="Select payer's bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {payshapBanks.map((bank) => (
                        <SelectItem 
                          key={bank} 
                          value={bank}
                          style={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '14px'
                          }}
                        >
                          {bank}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.payerBankName && (
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '12px',
                      color: '#dc2626',
                      margin: '4px 0 0 0'
                    }}>
                      {errors.payerBankName}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="payerAccountNumber" style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    display: 'block'
                  }}>
                    Payer Account Number
                  </Label>
                  <Input
                    id="payerAccountNumber"
                    type="text"
                    inputMode="numeric"
                    placeholder={
                      formData.payerBankName && BANK_ACCOUNT_RULES[formData.payerBankName]
                        ? `Enter ${BANK_ACCOUNT_RULES[formData.payerBankName].label} digit account number`
                        : "Payer's bank account number"
                    }
                    value={formData.payerAccountNumber || ''}
                    onChange={(e) => handleInputChange('payerAccountNumber', e.target.value.replace(/\D/g, ''))}
                    style={{
                      height: '44px',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: '400',
                      borderRadius: '12px',
                      border: `1px solid ${errors.payerAccountNumber ? '#dc2626' : '#d1d5db'}`,
                      backgroundColor: '#f9fafb'
                    }}
                  />
                  {formData.payerBankName && BANK_ACCOUNT_RULES[formData.payerBankName] && !errors.payerAccountNumber && (
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '11px',
                      color: '#6b7280',
                      margin: '4px 0 0 0'
                    }}>
                      {formData.payerBankName} accounts have {BANK_ACCOUNT_RULES[formData.payerBankName].label} digits
                      {formData.payerAccountNumber ? ` — you entered ${formData.payerAccountNumber.replace(/\D/g, '').length}` : ''}
                    </p>
                  )}
                  {errors.payerAccountNumber && (
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '12px',
                      color: '#dc2626',
                      margin: '4px 0 0 0'
                    }}>
                      {errors.payerAccountNumber}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Amount */}
            <div>
              <Label htmlFor="amount" style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px',
                display: 'block'
              }}>
                Amount to Request (ZAR)
              </Label>
              <Input
                id="amount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={formData.amount}
                onChange={(e) => {
                  // Banking-grade: Preserve exact user input - NO auto-formatting or rounding
                  let inputValue = e.target.value;
                  
                  // Allow empty string, numbers, and single decimal point only
                  // Remove any currency symbols or spaces that user might type
                  inputValue = inputValue.replace(/[^\d.]/g, '');
                  
                  // Ensure only one decimal point
                  const parts = inputValue.split('.');
                  if (parts.length > 2) {
                    inputValue = parts[0] + '.' + parts.slice(1).join('');
                  }
                  
                  // Limit to 2 decimal places (user can type more, but we'll show only 2)
                  // However, we preserve the exact input to prevent auto-changes
                  if (parts.length === 2 && parts[1].length > 2) {
                    // Only trim if user is typing, but preserve their intent
                    const decimalPart = parts[1].substring(0, 2);
                    inputValue = parts[0] + '.' + decimalPart;
                  }
                  
                  // Set exact value - no automatic modification
                  handleInputChange('amount', inputValue);
                }}
                onBlur={(e) => {
                  // Only validate/format on blur, not during typing
                  const value = e.target.value.trim();
                  if (value) {
                    const num = parseFloat(value);
                    if (!isNaN(num) && num > 0) {
                      // Optional: Format to 2 decimals on blur for display consistency
                      // But only if user typed a valid number
                      if (value !== num.toString() && value.includes('.')) {
                        handleInputChange('amount', num.toFixed(2));
                      }
                    }
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent browser auto-formatting with number input
                  // Block 'e', 'E', '+', '-' which are allowed in number inputs
                  if (['e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => {
                  // Prevent scrolling from changing number input values
                  e.currentTarget.blur();
                }}
                style={{
                  height: '44px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderRadius: '12px',
                  border: `1px solid ${errors.amount ? '#dc2626' : '#d1d5db'}`,
                  backgroundColor: '#f9fafb'
                }}
              />
              {errors.amount && (
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  color: '#dc2626',
                  margin: '4px 0 0 0'
                }}>
                  {errors.amount}
                </p>
              )}
              {formData.accountType === 'bank' && !errors.amount && (
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '11px',
                  color: '#6b7280',
                  margin: '4px 0 0 0'
                }}>
                  Minimum R10 for PayShap bank requests
                </p>
              )}
            </div>

            {/* Reference */}
            <div>
              <Label htmlFor="reference" style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px',
                display: 'block'
              }}>
                Payment Reference (Optional, max 20 chars)
              </Label>
              <Input
                id="reference"
                type="text"
                placeholder="e.g. Rent Aug, Lunch money"
                maxLength={20}
                value={formData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                style={{
                  height: '44px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderRadius: '12px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f9fafb'
                }}
              />
            </div>

            {/* MSISDN (Requester's Mobile Number) - Read-only, pre-populated */}
            <div>
              <Label htmlFor="requesterMsisdn" style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px',
                display: 'block'
              }}>
                Your Mobile Number (MSISDN) <span style={{ color: '#6b7280', fontSize: '12px', fontWeight: '400' }}>(Auto-filled)</span>
              </Label>
              <Input
                id="requesterMsisdn"
                type="text"
                value={user?.phoneNumber || ''}
                disabled
                readOnly
                style={{
                  height: '44px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderRadius: '12px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  cursor: 'not-allowed'
                }}
              />
              <p style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                This number will be sent to Standard Bank to identify your wallet when payment is received.
              </p>
            </div>

            {/* Recurring schedule fields */}
            {requestMode === 'recurring' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
                <div>
                  <Label style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px', display: 'block' }}>Frequency</Label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    {(['daily','weekly','monthly'] as const).map(f => (
                      <button key={f} onClick={() => handleInputChange('frequency', f)} style={{ padding: '10px', borderRadius: 8, border: `1px solid ${formData.frequency === f ? '#86BE41' : '#e5e7eb'}`, background: formData.frequency === f ? '#f0fdf4' : '#fff', cursor: 'pointer' }}>{f[0].toUpperCase()+f.slice(1)}</button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' }}>Start date</Label>
                    <Input type="date" value={formData.startDate} onChange={(e) => handleInputChange('startDate', e.target.value)} style={{ height: '44px', borderRadius: 12, backgroundColor: '#f9fafb', border: `1px solid ${errors.startDate ? '#dc2626' : '#d1d5db'}` }} />
                    {errors.startDate && <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors.startDate}</p>}
                  </div>
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' }}>Start time</Label>
                    <Input type="time" value={formData.startTime} onChange={(e) => handleInputChange('startTime', e.target.value)} style={{ height: '44px', borderRadius: 12, backgroundColor: '#f9fafb', border: `1px solid ${errors.startTime ? '#dc2626' : '#d1d5db'}` }} />
                    {errors.startTime && <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors.startTime}</p>}
                  </div>
                </div>
                {formData.frequency === 'weekly' && (
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' }}>Day of week</Label>
                    <Select value={formData.dayOfWeek} onValueChange={(v) => handleInputChange('dayOfWeek', v)}>
                      <SelectTrigger style={{ height: '44px', borderRadius: 12, backgroundColor: '#f9fafb', border: `1px solid ${errors.dayOfWeek ? '#dc2626' : '#d1d5db'}` }}>
                        <SelectValue placeholder="Select weekday" />
                      </SelectTrigger>
                      <SelectContent>
                        {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d, i) => (
                          <SelectItem key={d} value={String(i)} style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px' }}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.dayOfWeek && <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors.dayOfWeek}</p>}
                  </div>
                )}
                {formData.frequency === 'monthly' && (
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' }}>Day of month</Label>
                    <Input type="number" min={1} max={31} value={formData.dayOfMonth} onChange={(e) => handleInputChange('dayOfMonth', e.target.value)} style={{ height: '44px', borderRadius: 12, backgroundColor: '#f9fafb', border: `1px solid ${errors.dayOfMonth ? '#dc2626' : '#d1d5db'}` }} />
                    {errors.dayOfMonth && <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors.dayOfMonth}</p>}
                  </div>
                )}
                <div>
                  <Label style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' }}>Ends</Label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    {(['never','count','until'] as const).map(opt => (
                      <button key={opt} onClick={() => handleInputChange('endOption', opt)} style={{ padding: '10px', borderRadius: 8, border: `1px solid ${formData.endOption === opt ? '#86BE41' : '#e5e7eb'}`, background: formData.endOption === opt ? '#f0fdf4' : '#fff', cursor: 'pointer' }}>{opt[0].toUpperCase()+opt.slice(1)}</button>
                    ))}
                  </div>
                </div>
                {formData.endOption === 'count' && (
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' }}>Occurrences</Label>
                    <Input type="number" min={1} max={60} value={formData.occurrences} onChange={(e) => handleInputChange('occurrences', e.target.value)} style={{ height: '44px', borderRadius: 12, backgroundColor: '#f9fafb', border: `1px solid ${errors.occurrences ? '#dc2626' : '#d1d5db'}` }} />
                    {errors.occurrences && <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors.occurrences}</p>}
                  </div>
                )}
                {formData.endOption === 'until' && (
                  <div>
                    <Label style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6, display: 'block' }}>Until date</Label>
                    <Input type="date" value={formData.untilDate} onChange={(e) => handleInputChange('untilDate', e.target.value)} style={{ height: '44px', borderRadius: 12, backgroundColor: '#f9fafb', border: `1px solid ${errors.untilDate ? '#dc2626' : '#d1d5db'}` }} />
                    {errors.untilDate && <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors.untilDate}</p>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid #f1f5f9'
          }}>
            <Button 
              variant="outline"
              onClick={handleCancel}
              style={{
                flex: 1,
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '12px',
                padding: '16px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                minHeight: '44px'
              }}
            >
              Cancel
            </Button>
            
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <Button 
                onClick={() => setShowDialog(true)}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  minHeight: '44px'
                }}
              >
                Send Request
              </Button>

              <DialogContent style={{
                fontFamily: 'Montserrat, sans-serif',
                maxWidth: '340px',
                borderRadius: '16px'
              }}>
                <DialogDescription className="sr-only">
                  Send a payment request to a contact
                </DialogDescription>
                <DialogHeader>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                  }}>
                    <DialogTitle style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      Send Payment Request
                    </DialogTitle>
                    <button
                      onClick={() => setShowDialog(false)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: 'transparent',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <X style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                    </button>
                  </div>
                </DialogHeader>

                {/* Confirmation Details */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        Request from
                      </span>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#1f2937'
                      }}>
                        {formData.payerName}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        Account type
                      </span>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#1f2937'
                      }}>
                        {formData.accountType === 'mymoolah' ? 'MyMoolah' : 'Bank'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        Amount
                      </span>
                      <span style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#1f2937'
                      }}>
                        {formatCurrency(formData.amount)}
                      </span>
                    </div>
                  </div>

                  {/* Information message */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <Bell style={{ width: '16px', height: '16px', color: '#2D8CCA', marginTop: '2px', flexShrink: 0 }} />
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '12px',
                      color: '#1e40af',
                      margin: '0',
                      lineHeight: '1.4'
                    }}>
                      {getNotificationMessage()}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Button 
                      variant="outline"
                      onClick={() => setShowDialog(false)}
                      style={{
                        flex: 1,
                        backgroundColor: 'transparent',
                        color: '#6b7280',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '12px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        minHeight: '44px'
                      }}
                    >
                      Cancel
                    </Button>
                    
                    <Button 
                      onClick={() => {
                        if (requestMode === 'recurring') {
                          // Call recurring create endpoint
                          (async () => {
                            try {
                              setIsProcessing(true);
                              const token = getToken();
                              const api = APP_CONFIG.API.baseUrl;
                              const amount = parseFloat(formData.amount);
                              const resp = await fetch(`${api}/api/v1/requests/wallet/recurring`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                body: JSON.stringify({
                                  payerPhoneNumber: normalizeSAMobile(formData.payerMobileNumber),
                                  amount,
                                  description: formData.reference || undefined,
                                  frequency: formData.frequency,
                                  dayOfWeek: formData.frequency === 'weekly' ? Number(formData.dayOfWeek) : undefined,
                                  dayOfMonth: formData.frequency === 'monthly' ? Number(formData.dayOfMonth) : undefined,
                                  startDate: formData.startDate,
                                  startTime: formData.startTime,
                                  endOption: formData.endOption,
                                  occurrences: formData.endOption === 'count' ? Number(formData.occurrences) : undefined,
                                  untilDate: formData.endOption === 'until' ? formData.untilDate : undefined,
                                })
                              });
                              const j = await resp.json();
                              if (!resp.ok || !j?.success) throw new Error(j?.message || 'Failed to create schedule');
                              
                              // Create a mock request object for the success page
                              const recurringRequest: MoneyRequest = {
                                id: j.data.recurringId,
                                type: 'money_request',
                                accountType: formData.accountType,
                                requesterId: user?.id?.toString() || '1',
                                requesterName: user?.name || 'User',
                                payerName: formData.payerName,
                                payerMobileNumber: formData.payerMobileNumber,
                                payerAccountNumber: formData.payerAccountNumber,
                                payerBankName: formData.payerBankName,
                                amount: parseFloat(formData.amount),
                                reference: formData.reference || '',
                                status: 'pending',
                                createdAt: new Date().toISOString(),
                                expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours from now
                              };
                              
                              setCreatedRequest(recurringRequest);
                              setCurrentStep('success');
                              setShowDialog(false);

                              // Offer to save the payer for next time (only if new).
                              maybePromptSavePayer();

                              // Event-driven balance refresh after recurring request creation
                              try {
                                await refreshBalanceAfterAction('payment_request_created');
                              } catch (error) {
                                console.error('Balance refresh failed after recurring request creation:', error);
                                // Don't break the success flow if balance refresh fails
                              }
                            } catch (e) {
                              console.error('Recurring request failed:', e);
                              alert('Failed to create recurring schedule. Please try again.');
                              setShowDialog(false);
                            } finally {
                              setIsProcessing(false);
                            }
                          })();
                          return;
                        }
                        handleRequestMoney();
                      }}
                      disabled={isProcessing}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        minHeight: '44px',
                        opacity: isProcessing ? 0.7 : 1
                      }}
                    >
                      {isProcessing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Download style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                          Sending Request...
                        </div>
                      ) : (
                        'Send Request'
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </>
      )}

      {/* Remove Payer Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemovePayerModal}
        onClose={() => {
          setShowRemovePayerModal(false);
          setPayerToRemove(null);
        }}
        onConfirm={() => {
          if (payerToRemove) {
            const payerUserId = parseInt(payerToRemove.id);
            const hidePayer = async () => {
              const token = getToken();
              const api = APP_CONFIG.API.baseUrl;
              const resp = await fetch(`${api}/api/v1/requests/recent-payers/hide`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ payerUserId })
              });
              // Even if backend fails, optimistically hide client-side to avoid re-show until reload
              setRecentPayers(prev => prev.filter(p => p.payerUserId !== payerUserId));
              setShowRemovePayerModal(false);
              setPayerToRemove(null);
              if (!resp.ok) {
                console.error('Failed to hide payer server-side');
              }
            };
            hidePayer();
          }
        }}
        title="Remove Payer"
        message="Are you sure you want to remove"
        confirmText="Yes, remove"
        cancelText="Cancel"
        type="danger"
        beneficiaryName={payerToRemove?.name}
      />

      {/* Remove from Payers (shared contact book) — clears payer role only */}
      <ConfirmationModal
        isOpen={showRemoveSavedPayerModal}
        onClose={() => {
          setShowRemoveSavedPayerModal(false);
          setSavedPayerToRemove(null);
        }}
        onConfirm={() => {
          if (savedPayerToRemove) {
            const idToRemove = savedPayerToRemove.id;
            const removePayerRole = async () => {
              try {
                await beneficiaryService.setBeneficiaryRole(idToRemove, { isPayer: false });
              } catch (e) {
                console.error('Failed to remove payer role server-side', e);
              }
              // Optimistically drop from the visible list, then reload from server.
              setSavedPayers((prev) => prev.filter((p) => String(p.id) !== String(idToRemove)));
              setShowRemoveSavedPayerModal(false);
              setSavedPayerToRemove(null);
              await loadSavedPayers();
            };
            void removePayerRole();
          }
        }}
        title="Remove from Payers"
        message="This won't delete the contact or affect Send Money. Remove"
        confirmText="Yes, remove"
        cancelText="Cancel"
        type="danger"
        beneficiaryName={savedPayerToRemove?.name}
      />
      <ErrorModal
        isOpen={!!identifierError}
        onClose={() => setIdentifierError(null)}
        title={identifierError?.title || 'Invalid details'}
        message={identifierError?.message || ''}
        type="error"
      />
    </div>
  );
}